import React, { useRef, useEffect, useState } from 'react';
import Styles from './styles.module.scss';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const Hero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPoster, setShowPoster] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [daysUntilEvent, setDaysUntilEvent] = useState<number>(0);
  const [videoSrc, setVideoSrc] = useState('/images/2025/hero__movie__sp.mp4');

  // スクロールアニメーション用のrefs
  const { ref: bg0Ref, isVisible: isBg0Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });
  const { ref: bg1Ref, isVisible: isBg1Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });
  const { ref: bg2Ref, isVisible: isBg2Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg3Ref, isVisible: isBg3Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg4Ref, isVisible: isBg4Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg5Ref, isVisible: isBg5Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg6Ref, isVisible: isBg6Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg7Ref, isVisible: isBg7Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg8Ref, isVisible: isBg8Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg9Ref, isVisible: isBg9Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg10Ref, isVisible: isBg10Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg11Ref, isVisible: isBg11Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg12Ref, isVisible: isBg12Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg13Ref, isVisible: isBg13Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg14Ref, isVisible: isBg14Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { ref: bg15Ref, isVisible: isBg15Visible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.3,
    triggerOnce: true,
  });

  // 画面サイズに応じてビデオソースを切り替え
  useEffect(() => {
    const updateVideoSrc = () => {
      const isMobile = window.innerWidth <= 1024;
      const newSrc = isMobile ? '/images/2025/hero__movie__sp.mp4' : '/images/2025/hero__movie.mp4';

      if (newSrc !== videoSrc) {
        setVideoSrc(newSrc);
      }
    };

    updateVideoSrc();
    window.addEventListener('resize', updateVideoSrc);

    return () => {
      window.removeEventListener('resize', updateVideoSrc);
    };
  }, [videoSrc]);

  // イベント開催日（2025年11月2日）までの日数を計算
  useEffect(() => {
    const calculateDaysUntil = () => {
      const eventDate = new Date('2025-11-02');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);

      const diffTime = eventDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays > 0 ? diffDays : 0;
    };

    setDaysUntilEvent(calculateDaysUntil());

    // 日付が変わった時のために、毎日0時に更新
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const timeout = setTimeout(() => {
      setDaysUntilEvent(calculateDaysUntil());
      // その後24時間ごとに更新
      const interval = setInterval(
        () => {
          setDaysUntilEvent(calculateDaysUntil());
        },
        24 * 60 * 60 * 1000,
      );

      return () => clearInterval(interval);
    }, tomorrow.getTime() - now.getTime());

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const startVideo = () => {
      video
        .play()
        .then(() => {
          setVideoPlaying(true);
          setTimeout(() => {
            setShowPoster(false);
          }, 1000);
        })
        .catch(() => {
          const handleFirstTouch = () => {
            video.play().then(() => {
              setVideoPlaying(true);
              setTimeout(() => {
                setShowPoster(false);
              }, 1000);
            });
          };

          document.addEventListener('touchstart', handleFirstTouch, { once: true });
          document.addEventListener('click', handleFirstTouch, { once: true });
        });
    };

    if (video.readyState >= 3) {
      setTimeout(startVideo, 1500);
    } else {
      const handleCanPlay = () => {
        setTimeout(startVideo, 1500);
      };
      video.addEventListener('canplaythrough', handleCanPlay, { once: true });

      return () => {
        video.removeEventListener('canplaythrough', handleCanPlay);
      };
    }
  }, []);

  return (
    <div className={Styles.hero}>
      <div className={Styles.hero__inner}>
        <div className={Styles.hero__inner__main}>
          {showPoster && (
            <img
              src="/images/2025/hero__poster.webp"
              alt=""
              className={`${Styles.hero__inner__main__poster} ${videoPlaying ? Styles['hero__inner__main__poster--fadeout'] : ''}`}
            />
          )}
          <video
            ref={videoRef}
            className={Styles.hero__inner__main__video}
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        </div>
        <div className={Styles.hero__inner__day}>
          {daysUntilEvent > 0 ? (
            <>
              <p className={Styles.hero__inner__day__label}>あと</p>
              <p className={Styles.hero__inner__day__number}>
                <strong className={Styles.hero__inner__day__day}>{daysUntilEvent}</strong>
                <span className={Styles.hero__inner__day__unit}>日</span>
              </p>
            </>
          ) : (
            <>
              <p className={Styles.hero__inner__day__number}>
                <strong className={Styles.hero__inner__day__day}>開催終了</strong>
              </p>
            </>
          )}
        </div>
      </div>
      <div className={Styles.hero__bg}>
        <div
          ref={bg0Ref}
          className={`${Styles.hero__bg__0} ${isBg0Visible ? Styles['hero__bg__0--visible'] : ''}`}
        >
          <svg
            width="103"
            height="103"
            viewBox="0 0 103 103"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="51.5"
              cy="51.5"
              r="49.5768"
              stroke="#F1ECE6"
              strokeWidth="3.84647"
              strokeDasharray="7.69 7.69"
            />
          </svg>
        </div>
        <div
          ref={bg1Ref}
          className={`${Styles.hero__bg__1} ${isBg1Visible ? Styles['hero__bg__1--visible'] : ''}`}
        >
          <svg
            width="161"
            height="102"
            viewBox="0 0 161 102"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="152.513" cy="7.51302" r="7.51302" fill="#F6B8DF" />
            <rect
              y="85.6886"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 0 85.6886)"
              fill="url(#paint0_linear_5229_398)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5229_398"
                x1="0"
                y1="94.8189"
                x2="152.577"
                y2="94.8189"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#3F78FB" />
                <stop offset="1" stopColor="#F6B8DF" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg2Ref}
          className={`${Styles.hero__bg__2} ${isBg2Visible ? Styles['hero__bg__2--visible'] : ''}`}
        >
          <svg
            width="250"
            height="141"
            viewBox="0 0 250 141"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="154.5" cy="59.5" r="15.5" fill="#9997ED" />
            <path
              d="M224.061 89.0768C224.115 88.9627 224.284 88.9849 224.307 89.1093L227.239 104.956C227.31 105.338 227.619 105.631 228.003 105.682L243.98 107.79C244.106 107.806 244.137 107.974 244.026 108.034L229.861 115.72C229.519 115.905 229.336 116.289 229.406 116.67L232.339 132.517C232.362 132.642 232.212 132.723 232.12 132.636L220.433 121.539C220.152 121.272 219.73 121.216 219.389 121.402L205.223 129.087C205.112 129.147 204.99 129.03 205.044 128.916L211.986 114.372C212.153 114.021 212.076 113.603 211.794 113.336L200.108 102.239C200.016 102.152 200.089 101.999 200.214 102.015L216.192 104.123C216.577 104.174 216.951 103.971 217.118 103.621L224.061 89.0768Z"
              fill="white"
              stroke="#1549B6"
              strokeWidth="0.765608"
            />
            <path
              d="M33.3576 17.4724C33.3548 17.2963 33.5784 17.2194 33.6845 17.3599L47.2016 35.307C47.5272 35.7394 48.0944 35.9128 48.6062 35.7367L69.8519 28.4276C70.0185 28.3706 70.1607 28.5598 70.0596 28.7042L57.168 47.106C56.8577 47.5492 56.8674 48.142 57.1929 48.5742L70.71 66.5213C70.816 66.6622 70.6797 66.8562 70.511 66.8046L49.0269 60.23C48.5094 60.0717 47.9486 60.2646 47.638 60.7078L34.7461 79.1087C34.6449 79.2531 34.4181 79.1841 34.415 79.0078L34.0293 56.5432C34.0199 56.0022 33.6629 55.5289 33.1456 55.3704L11.6611 48.7949C11.4927 48.7433 11.4883 48.5067 11.6547 48.4491L32.9003 41.14C33.4122 40.9639 33.7526 40.4782 33.7433 39.937L33.3576 17.4724Z"
              fill="white"
              stroke="#1549B6"
              strokeWidth="1.06735"
            />
            <circle cx="150.5" cy="7.5" r="7" fill="white" stroke="#1549B6" />
          </svg>
        </div>
        <div
          ref={bg3Ref}
          className={`${Styles.hero__bg__3} ${isBg3Visible ? Styles['hero__bg__3--visible'] : ''}`}
        >
          <svg
            width="185"
            height="90"
            viewBox="0 0 185 90"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              y="85.6886"
              width="152.577"
              height="4.87832"
              rx="2.43916"
              transform="rotate(-29.7401 0 85.6886)"
              fill="#F6B8DF"
            />
            <rect
              x="50"
              y="75.6886"
              width="152.577"
              height="4.87832"
              rx="2.43916"
              transform="rotate(-29.7401 50 75.6886)"
              fill="#F6B8DF"
            />
          </svg>
        </div>
        <div
          ref={bg4Ref}
          className={`${Styles.hero__bg__4} ${isBg4Visible ? Styles['hero__bg__4--visible'] : ''}`}
        >
          <svg
            width="241"
            height="227"
            viewBox="0 0 241 227"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="71" r="10.5" fill="white" stroke="#1549B6" />
            <path
              d="M89.5528 63.777C89.6076 63.6297 89.8188 63.6385 89.8611 63.7899L97.4708 91.0436C97.6005 91.5081 98.0143 91.8364 98.4962 91.8568L126.768 93.0416C126.925 93.0484 126.981 93.2523 126.851 93.3393L103.283 108.998C102.881 109.265 102.697 109.76 102.827 110.225L110.435 137.479C110.478 137.63 110.302 137.747 110.179 137.65L88.0028 120.074C87.6247 119.774 87.0972 119.752 86.6953 120.019L63.1264 135.678C62.9955 135.765 62.8295 135.633 62.8843 135.486L72.7475 108.965C72.9157 108.512 72.7738 108.003 72.3957 107.704L50.2196 90.1278C50.0965 90.0302 50.1705 89.8316 50.3276 89.8381L78.5986 91.0229C79.0807 91.0431 79.5213 90.7506 79.6895 90.2984L89.5528 63.777Z"
              fill="white"
              stroke="#1549B6"
              strokeWidth="0.951418"
            />
            <path
              d="M171.04 155.824C170.95 155.181 171.736 154.798 172.187 155.266L186.804 170.413C187.005 170.623 187.319 170.678 187.58 170.551L206.503 161.331C207.087 161.046 207.694 161.675 207.389 162.248L197.499 180.83C197.363 181.087 197.407 181.402 197.608 181.611L212.225 196.759C212.676 197.226 212.265 197.998 211.626 197.885L190.897 194.221C190.611 194.171 190.325 194.31 190.188 194.567L180.299 213.149C179.993 213.722 179.133 213.57 179.043 212.927L176.121 192.081C176.081 191.793 175.86 191.564 175.574 191.513L154.845 187.85C154.206 187.737 154.084 186.871 154.668 186.587L173.591 177.366C173.853 177.239 174.002 176.958 173.962 176.671L171.04 155.824Z"
              fill="#F6B8DF"
            />
            <circle cx="234.5" cy="111.5" r="6" fill="white" stroke="#1549B6" />
            <path
              d="M17.3027 156.161C17.167 155.697 17.7044 155.331 18.0869 155.626L21.3662 158.161C21.5372 158.293 21.774 158.3 21.9526 158.179L25.3767 155.843C25.7761 155.571 26.2907 155.968 26.1277 156.424L24.7304 160.326C24.6575 160.529 24.724 160.757 24.895 160.889L28.1743 163.424C28.5568 163.719 28.3375 164.332 27.8542 164.317L23.7114 164.194C23.4952 164.188 23.2996 164.321 23.2267 164.525L21.8293 168.427C21.6663 168.882 21.0161 168.863 20.8804 168.399L19.7174 164.42C19.6567 164.213 19.4693 164.068 19.2532 164.062L15.1103 163.938C14.627 163.924 14.4445 163.3 14.8439 163.027L18.2679 160.692C18.4466 160.57 18.5264 160.347 18.4658 160.139L17.3027 156.161Z"
              fill="#3172F8"
            />
            <circle cx="199.185" cy="16.185" r="16.185" fill="#F6B8DF" />
          </svg>
        </div>
        <div
          ref={bg5Ref}
          className={`${Styles.hero__bg__5} ${isBg5Visible ? Styles['hero__bg__5--visible'] : ''}`}
        >
          <svg
            width="594"
            height="390"
            viewBox="0 0 594 390"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-21"
              y="309.371"
              width="665.98"
              height="116.57"
              rx="58.2848"
              transform="rotate(-29.7401 -21 309.371)"
              fill="url(#paint0_linear_5229_407)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5229_407"
                x1="-21"
                y1="367.655"
                x2="644.98"
                y2="367.655"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A81FE" />
                <stop offset="1" stopColor="white" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg6Ref}
          className={`${Styles.hero__bg__6} ${isBg6Visible ? Styles['hero__bg__6--visible'] : ''}`}
        >
          <svg
            width="259"
            height="186"
            viewBox="0 0 259 186"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-16"
              y="123.028"
              width="282.981"
              height="91.1066"
              rx="45.5533"
              transform="rotate(-29.4259 -16 123.028)"
              fill="url(#paint0_linear_5229_409)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5229_409"
                x1="-16"
                y1="168.582"
                x2="266.981"
                y2="168.582"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#D199ED" />
                <stop offset="1" stopColor="#CEDBFF" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg7Ref}
          className={`${Styles.hero__bg__7} ${isBg7Visible ? Styles['hero__bg__7--visible'] : ''}`}
        >
          <svg
            width="163"
            height="100"
            viewBox="0 0 163 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              y="83.6886"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 0 83.6886)"
              fill="#0B48F2"
            />
            <circle cx="154.513" cy="7.51302" r="7.51302" fill="#0A46F1" />
          </svg>
        </div>
        <div
          ref={bg8Ref}
          className={`${Styles.hero__bg__8} ${isBg8Visible ? Styles['hero__bg__8--visible'] : ''}`}
        >
          <svg
            width="729"
            height="198"
            viewBox="0 0 729 198"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="148.5" cy="149.5" r="3.5" fill="#F6B8DF" />
            <circle cx="162.513" cy="189.513" r="7.51302" fill="#6593FF" />
            <rect
              x="383.166"
              y="41.3391"
              width="391.673"
              height="43.9796"
              rx="21.9898"
              transform="rotate(160.045 383.166 41.3391)"
              fill="url(#paint0_linear_5236_445)"
            />
            <path
              d="M309.465 147.072C309.749 146.712 310.327 146.925 310.31 147.383L309.731 162.227C309.723 162.432 309.848 162.619 310.041 162.69L323.98 167.827C324.41 167.986 324.386 168.602 323.945 168.726L309.648 172.764C309.451 172.819 309.312 172.996 309.304 173.201L308.726 188.045C308.708 188.503 308.114 188.671 307.859 188.29L299.602 175.941C299.488 175.77 299.277 175.692 299.08 175.748L284.783 179.785C284.342 179.91 283.999 179.397 284.283 179.037L293.476 167.368C293.603 167.207 293.612 166.982 293.498 166.812L285.24 154.462C284.985 154.081 285.367 153.597 285.797 153.755L299.736 158.892C299.929 158.963 300.145 158.902 300.272 158.741L309.465 147.072Z"
              fill="white"
            />
            <rect
              x="360.713"
              y="180.792"
              width="390.749"
              height="10.7064"
              rx="5.35321"
              transform="rotate(-21.5264 360.713 180.792)"
              fill="white"
            />
            <rect
              x="333.736"
              y="147.606"
              width="390.749"
              height="16.4619"
              rx="8.23097"
              transform="rotate(-20.3607 333.736 147.606)"
              fill="white"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5236_445"
                x1="383.166"
                y1="63.3288"
                x2="774.839"
                y2="63.3288"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#9D99ED" />
                <stop offset="1" stopColor="#124EF3" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg9Ref}
          className={`${Styles.hero__bg__9} ${isBg9Visible ? Styles['hero__bg__9--visible'] : ''}`}
        >
          <svg
            width="637"
            height="432"
            viewBox="0 0 637 432"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              y="330.371"
              width="665.98"
              height="116.57"
              rx="58.2848"
              transform="rotate(-29.7401 0 330.371)"
              fill="url(#paint0_linear_5236_449)"
            />
            <rect
              x="218"
              y="162.389"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 218 162.389)"
              fill="white"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5236_449"
                x1="0"
                y1="388.656"
                x2="665.98"
                y2="388.656"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A81FE" />
                <stop offset="1" stopColor="white" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg10Ref}
          className={`${Styles.hero__bg__10} ${isBg10Visible ? Styles['hero__bg__10--visible'] : ''}`}
        >
          <svg
            width="554"
            height="528"
            viewBox="0 0 554 528"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="164.031"
              y="159.346"
              width="256.385"
              height="8.19733"
              rx="4.09867"
              transform="rotate(-26.2799 164.031 159.346)"
              fill="#F6B8DF"
            />
            <rect
              y="447.946"
              width="584.057"
              height="91.1066"
              rx="45.5533"
              transform="rotate(-29.4259 0 447.946)"
              fill="url(#paint0_linear_5236_452)"
            />
            <path
              d="M429.774 6.98852C429.866 6.79693 430.15 6.83445 430.188 7.0432L433.952 27.3852C434.071 28.0267 434.59 28.5193 435.236 28.6047L455.745 31.3108C455.956 31.3386 456.008 31.6193 455.821 31.7207L437.638 41.586C437.065 41.8972 436.757 42.5431 436.876 43.1846L440.64 63.5256C440.678 63.7346 440.427 63.8718 440.272 63.7254L425.272 49.4805C424.799 49.0313 424.09 48.9379 423.516 49.2489L405.333 59.1152C405.146 59.2163 404.939 59.0188 405.03 58.827L413.942 40.1585C414.223 39.5697 414.093 38.8661 413.62 38.4168L398.618 24.1728C398.464 24.0264 398.587 23.769 398.797 23.7966L419.307 26.5028C419.954 26.588 420.582 26.2467 420.863 25.658L429.774 6.98852Z"
              fill="white"
              stroke="#1549B6"
              strokeWidth="1.2865"
            />
            <path
              d="M346.925 142.125C346.835 141.482 347.621 141.099 348.072 141.567L356.76 150.571C356.962 150.78 357.276 150.836 357.537 150.708L368.785 145.228C369.369 144.943 369.976 145.572 369.671 146.145L363.792 157.191C363.656 157.448 363.7 157.763 363.902 157.972L372.59 166.976C373.041 167.444 372.631 168.215 371.991 168.102L359.669 165.925C359.383 165.874 359.097 166.013 358.96 166.27L353.082 177.316C352.776 177.889 351.916 177.737 351.826 177.094L350.089 164.702C350.049 164.414 349.828 164.185 349.542 164.135L337.22 161.957C336.58 161.844 336.459 160.978 337.043 160.694L348.291 155.213C348.553 155.086 348.702 154.805 348.662 154.517L346.925 142.125Z"
              fill="#F6B8DF"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5236_452"
                x1="0"
                y1="493.499"
                x2="584.057"
                y2="493.499"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#D199ED" />
                <stop offset="1" stopColor="#CEDBFF" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg11Ref}
          className={`${Styles.hero__bg__11} ${isBg11Visible ? Styles['hero__bg__11--visible'] : ''}`}
        >
          <svg
            width="827"
            height="483"
            viewBox="0 0 827 483"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              y="377.531"
              width="862.346"
              height="116.57"
              rx="58.2848"
              transform="rotate(-25.9633 0 377.531)"
              fill="url(#paint0_linear_5236_454)"
            />
            <rect
              x="266"
              y="320.689"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 266 320.689)"
              fill="#0B48F2"
            />
            <circle cx="420.513" cy="244.513" r="7.51302" fill="#0A46F1" />
            <path
              d="M464.701 239.093C464.611 238.449 465.397 238.067 465.848 238.534L471.043 243.919C471.245 244.128 471.558 244.183 471.82 244.056L478.546 240.778C479.13 240.494 479.737 241.123 479.432 241.696L475.916 248.301C475.78 248.558 475.824 248.873 476.026 249.082L481.221 254.467C481.672 254.934 481.262 255.706 480.622 255.593L473.254 254.29C472.968 254.24 472.682 254.379 472.545 254.636L469.03 261.241C468.725 261.814 467.864 261.662 467.774 261.019L466.735 253.609C466.695 253.321 466.474 253.092 466.188 253.042L458.82 251.739C458.18 251.626 458.059 250.761 458.643 250.476L465.369 247.199C465.63 247.071 465.78 246.791 465.74 246.503L464.701 239.093Z"
              fill="#F6B8DF"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5236_454"
                x1="0"
                y1="435.816"
                x2="862.347"
                y2="435.816"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0F58FF" />
                <stop offset="1" stopColor="#6D99FF" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div
          ref={bg12Ref}
          className={`${Styles.hero__bg__12} ${isBg12Visible ? Styles['hero__bg__12--visible'] : ''}`}
        >
          <svg
            width="270"
            height="270"
            viewBox="0 0 270 270"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="135"
              cy="135"
              r="132.124"
              stroke="#F1ECE6"
              strokeWidth="5.75104"
              strokeDasharray="11.5 11.5"
            />
          </svg>
        </div>
        <div
          ref={bg13Ref}
          className={`${Styles.hero__bg__13} ${isBg13Visible ? Styles['hero__bg__13--visible'] : ''}`}
        >
          <svg
            width="270"
            height="270"
            viewBox="0 0 270 270"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="135"
              cy="135"
              r="132.124"
              stroke="#F1ECE6"
              strokeWidth="5.75104"
              strokeDasharray="11.5 11.5"
            />
          </svg>
        </div>
        <div
          ref={bg14Ref}
          className={`${Styles.hero__bg__14} ${isBg14Visible ? Styles['hero__bg__14--visible'] : ''}`}
        ></div>
        <div
          ref={bg15Ref}
          className={`${Styles.hero__bg__15} ${isBg15Visible ? Styles['hero__bg__15--visible'] : ''}`}
        ></div>
      </div>
    </div>
  );
};

export default Hero;
